/**
 * sendAssetToTimeline — acción única compartida para mandar un asset al
 * Timeline Editor desde cualquier superficie (nodo del canvas, galería
 * principal, panel lateral de assets, selector del timeline).
 *
 * Reglas:
 *  - La identidad del archivo es el `assetId`; la URL guardada en el nodo es
 *    sólo fallback y se re-resuelve contra el backend en cada envío.
 *  - Si no hay `assetId` se intenta identificarlo por URL; si no se puede,
 *    error claro y no se agrega nada.
 *  - Video: duración real del asset. Imagen: 5 s inicial.
 *  - Doble clic: se ignora el segundo envío dentro de una ventana corta.
 */
import { toast } from 'sonner';
import axios from 'axios';
import useTimelineStore from '../store/timelineStore';
import { useWorkflowStore } from '../store/workflowStore';
import { fetchTimeline } from '../services/timelineSync';
import assetService from '../services/assetService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const IMAGE_CLIP_DEFAULT_DURATION = 5;
const DEDUPE_WINDOW_MS = 1500;
const PROBE_TIMEOUT_MS = 6000;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const recentSends = new Map();

let refreshNoticeShown = false;

export const __resetSendAssetGuards = () => {
  recentSends.clear();
  refreshNoticeShown = false;
};

const kindFromUrl = (url = '') => {
  const clean = String(url).split('?')[0].toLowerCase();
  if (/\.(mp4|mov|webm|mkv|m4v|avi)$/.test(clean)) return 'video';
  if (/\.(mp3|wav|ogg|flac|m4a|aac)$/.test(clean)) return 'audio';
  if (/\.(png|jpe?g|webp|gif|bmp|heic)$/.test(clean)) return 'image';
  return null;
};

const probeDuration = (url) =>
  new Promise((resolve) => {
    if (typeof document === 'undefined') return resolve(null);
    const el = document.createElement('video');
    const done = (value) => {
      el.removeAttribute('src');
      resolve(value);
    };
    const timer = setTimeout(() => done(null), PROBE_TIMEOUT_MS);
    el.preload = 'metadata';
    el.muted = true;
    el.onloadedmetadata = () => {
      clearTimeout(timer);
      done(Number.isFinite(el.duration) && el.duration > 0 ? el.duration : null);
    };
    el.onerror = () => {
      clearTimeout(timer);
      done(null);
    };
    el.src = url;
  });

const resolveAsset = async (assetId, fallbackUrl) => {
  try {
    if (UUID_RE.test(assetId || '')) return await assetService.getAsset(assetId);
  } catch (err) {
    // El asset pudo haber sido borrado: se intenta por URL más abajo.
  }
  try {
    if (fallbackUrl) return await assetService.lookupByUrl(fallbackUrl);
  } catch (err) {
    return null;
  }
  return null;
};

export async function sendAssetToTimeline(assetId, options = {}) {
  const {
    fallbackUrl = null,
    fallbackKind = null,
    label = null,
    track = null,
    thumbUrl = null,
    duration = null,
    onResolved = null,
    // E2 — origen del clip: { project_id, node_id }
    source = null,
  } = options;

  const dedupeKey = assetId || fallbackUrl;
  if (!dedupeKey) {
    toast.error('No se pudo identificar el archivo.', { position: 'bottom-right' });
    return null;
  }

  const now = Date.now();
  const last = recentSends.get(dedupeKey);
  if (last && now - last < DEDUPE_WINDOW_MS) return null;
  recentSends.set(dedupeKey, now);

  const asset = await resolveAsset(assetId, fallbackUrl);
  const url = asset?.url || fallbackUrl;
  const kind = asset?.media_kind || fallbackKind || kindFromUrl(url) || 'video';

  if (!url) {
    recentSends.delete(dedupeKey);
    toast.error('El archivo ya no está disponible. Volvé a generarlo o subilo de nuevo.', {
      position: 'bottom-right',
    });
    return null;
  }

  if (asset && fallbackKind && asset.media_kind && asset.media_kind !== fallbackKind) {
    recentSends.delete(dedupeKey);
    toast.error('La referencia del nodo no coincide con el archivo guardado.', {
      position: 'bottom-right',
    });
    return null;
  }

  if (asset && fallbackUrl && asset.url !== fallbackUrl) {
    if (!refreshNoticeShown) {
      refreshNoticeShown = true;
      toast.message('Se actualizó la referencia del archivo', { position: 'bottom-right' });
    }
  }
  if (asset && onResolved) onResolved(asset);

  const tl = useTimelineStore.getState();
  const rawLabel = label || asset?.filename ||
    (kind === 'image' ? 'Imagen' : kind === 'audio' ? 'Audio' : 'Video');
  const clipLabel = rawLabel.length > 22 ? `${rawLabel.slice(0, 19)}…` : rawLabel;

  let realDuration = duration ?? asset?.duration ?? null;
  if (!realDuration && kind !== 'image') realDuration = await probeDuration(url);

  // E3 · si el timeline viene de una plantilla, el próximo hueco vacío
  // compatible absorbe el asset (encaje `trim`: nunca cambia la velocidad).
  const openSlot = (tl.slots || []).find(
    (s) => !s.clip_id && (s.accepts || []).includes(kind));
  const projectId = options.projectId
    || useWorkflowStore.getState().currentProjectId;
  if (openSlot && projectId) {
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/timeline/fill-slot`,
        {
          slot_id: openSlot.slot_id,
          base_version: tl.serverVersion,
          asset: {
            source_url: url, kind, duration: realDuration,
            asset_id: asset?.id || null, label: clipLabel,
            thumb_url: asset?.thumbnail_url || thumbUrl || null,
            source: source || null,
          },
        });
      const store = useTimelineStore.getState();
      // La mutación devuelve la versión, no el doc: se relee del servidor.
      try {
        const remote = await fetchTimeline(projectId);
        if (remote) {
          store.hydrateTimeline(remote.data);
          store.setServerVersion(remote.version);
          store.setSyncStatus('saved');
        }
      } catch (_e) { /* el próximo autosave lo resincroniza */ }
      if (res.data?.result?.warning) {
        toast.warning(res.data.result.warning, { position: 'bottom-right' });
      } else {
        toast.success(`"${openSlot.name}" completado en el Timeline`, { position: 'bottom-right' });
      }
      store.openEditor();
      return { clipId: res.data?.result?.clip?.id, asset, url, kind, slotId: openSlot.slot_id };
    } catch (err) {
      // Sin servidor (offline) o hueco movido: cae al comportamiento de
      // siempre y va a su pista por defecto.
      if (err?.response?.status === 404 || err?.response?.status === 409 || !err?.response) {
        // sigue abajo
      } else {
        recentSends.delete(dedupeKey);
        toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail
          || 'No se pudo completar el hueco', { position: 'bottom-right' });
        return null;
      }
    }
  }

  const playhead = tl.playheadTime || 0;
  const meta = {
    source_url: url,
    original_duration: realDuration,
    label: clipLabel,
    thumb_url: asset?.thumbnail_url || thumbUrl || (kind === 'image' ? url : null),
    // E2 — trazabilidad: el clip recuerda de qué asset y de qué nodo salió.
    asset_id: asset?.id || (UUID_RE.test(assetId || '') ? assetId : null),
    source: source || null,
    ...(playhead > 0 ? { start_time: playhead } : {}),
  };

  let clipId;
  if (kind === 'audio') {
    clipId = tl.addAudioClip(meta);
  } else {
    clipId = tl.addVideoClip({
      ...meta,
      kind,
      ...(track ? { track } : {}),
      ...(kind === 'image' ? { duration: IMAGE_CLIP_DEFAULT_DURATION } : {}),
    });
  }

  tl.openEditor();
  toast.success('Agregado al Timeline', { position: 'bottom-right' });
  return { clipId, asset, url, kind };
}

export default sendAssetToTimeline;
